import React, { useState } from "react";
import { useSelector } from "react-redux";
import { apiConnector } from "../../../services/apiconnector";
import { aiEndpoints } from "../../../services/apis";

const { AI_DESCRIBE_COURSE_API } = aiEndpoints;

/**
 * CourseAIDescription
 * Shows an "AI Summary" panel on the CourseDetails page.
 * Lazy-loaded — only fetches when the user clicks "Generate".
 *
 * Props:
 *   courseTitle, description, sections, whatYouWillLearn, instructor (string)
 */
export default function CourseAIDescription({
  courseTitle,
  description,
  sections = [],
  whatYouWillLearn,
  instructor,
}) {
  const { token } = useSelector((s) => s.auth);
  const [aiData, setAiData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [generated, setGenerated] = useState(false);

  const generate = async () => {
    if (generated) return;
    setLoading(true);
    setError(null);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await apiConnector("POST", AI_DESCRIBE_COURSE_API, {
        courseTitle, description, sections, whatYouWillLearn, instructor,
      }, headers);
      if (res?.data?.success) {
        setAiData(res.data.data);
        setGenerated(true);
      } else {
        throw new Error(res?.data?.message || "Failed");
      }
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to generate AI summary. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="my-8 rounded-xl border border-yellow-500/30 bg-gradient-to-br from-yellow-900/10 to-richblack-800 p-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <span className="text-2xl">✨</span>
          <div>
            <p className="text-lg font-bold text-richblack-5">AI Course Overview</p>
            <p className="text-xs text-richblack-400">
              {generated ? "Generated by Gemini AI" : "Get an AI-powered breakdown of this course"}
            </p>
          </div>
        </div>
        {!generated && (
          <button
            onClick={generate}
            disabled={loading}
            className="flex items-center gap-2 rounded-lg bg-yellow-50 px-5 py-2.5
              text-sm font-semibold text-richblack-900 hover:bg-yellow-100
              disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? (
              <>
                <span className="w-4 h-4 border-2 border-richblack-600 border-t-richblack-900 rounded-full animate-spin" />
                Generating…
              </>
            ) : (
              <>✨ Generate AI Summary</>
            )}
          </button>
        )}
      </div>

      {/* Error */}
      {error && (
        <p className="mt-4 text-sm text-red-400 bg-red-900/20 rounded-lg px-4 py-3">
          {error}
        </p>
      )}

      {/* Loading skeleton */}
      {loading && !aiData && (
        <div className="mt-5 space-y-3 animate-pulse">
          {[80, 100, 60, 100, 70].map((w, i) => (
            <div key={i} className={`h-3 bg-richblack-700 rounded w-${w === 100 ? "full" : `[${w}%]`}`} />
          ))}
        </div>
      )}

      {/* AI Content */}
      {aiData && (
        <div className="mt-5 space-y-5">
          {/* Headline */}
          {aiData.headline && (
            <p className="text-xl font-bold text-yellow-50 leading-snug">
              {aiData.headline}
            </p>
          )}

          {/* Summary */}
          {aiData.summary && (
            <p className="text-richblack-100 text-sm leading-relaxed">{aiData.summary}</p>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
            {/* Key Topics */}
            {aiData.keyTopics?.length > 0 && (
              <div className="rounded-lg bg-richblack-800 p-4">
                <p className="text-xs font-semibold text-yellow-400 uppercase tracking-widest mb-3">
                  📚 Key Topics
                </p>
                <ul className="space-y-1.5">
                  {aiData.keyTopics.map((t, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-richblack-200">
                      <span className="text-yellow-400 mt-0.5">•</span>
                      {t}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="space-y-3">
              {/* Target Audience */}
              {aiData.targetAudience && (
                <div className="rounded-lg bg-richblack-800 p-4">
                  <p className="text-xs font-semibold text-blue-400 uppercase tracking-widest mb-1.5">
                    🎯 Who Is This For?
                  </p>
                  <p className="text-sm text-richblack-200">{aiData.targetAudience}</p>
                </div>
              )}

              {/* Prerequisites */}
              {aiData.prerequisites && (
                <div className="rounded-lg bg-richblack-800 p-4">
                  <p className="text-xs font-semibold text-purple-400 uppercase tracking-widest mb-1.5">
                    📋 Prerequisites
                  </p>
                  <p className="text-sm text-richblack-200">{aiData.prerequisites}</p>
                </div>
              )}
            </div>
          </div>

          {/* Outcome */}
          {aiData.outcome && (
            <div className="rounded-lg bg-green-900/20 border border-green-500/20 p-4">
              <p className="text-xs font-semibold text-green-400 uppercase tracking-widest mb-1.5">
                🏆 What You'll Achieve
              </p>
              <p className="text-sm text-richblack-100">{aiData.outcome}</p>
            </div>
          )}

          <p className="text-[11px] text-richblack-500 text-right">
            AI-generated summary · May not reflect exact course content
          </p>
        </div>
      )}
    </div>
  );
}
